/**
 * Sección Maestro: chat con asistente orquestador + visualizar organigrama.
 */
import { h, apiClient } from './_common.js';

export async function renderMaestro(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in max-w-5xl mx-auto' }, [
    h('div', { class: 'mb-6' }, [
      h('h2', { class: 'font-display text-3xl font-bold flex items-center gap-3' }, [
        h('span', {}, '🧠'),
        h('span', {}, 'Asistente Maestro · Orquestador'),
      ]),
      h('p', { class: 'text-ink-300 mt-1' }, 'El CEO IA de CARLOS OS · consulta a 1551 especialistas en 6 divisiones y sintetiza.'),
    ]),

    // Chat box
    h('div', { class: 'rounded-xl border border-base-700 bg-base-900/60 p-5 mb-6' }, [
      h('div', { class: 'flex items-center gap-2 mb-3' }, [
        h('label', { class: 'text-sm text-ink-400' }, 'Profundidad:'),
        h('select', { id: 'master-budget', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }, [
          h('option', { value: 'fast' }, '⚡ Rápida (haiku · 3-5s)'),
          h('option', { value: 'standard', selected: 'selected' }, '⚖️ Estándar (sonnet · 8-12s)'),
          h('option', { value: 'deep' }, '🧠 Profunda (opus · 20-30s)'),
        ]),
        h('span', { class: 'text-xs text-ink-500 ml-auto' }, 'Triage → fan-out (4 especialistas) → síntesis'),
      ]),
      h('textarea', { id: 'master-q', class: 'w-full bg-base-800 border border-base-600 rounded p-3 text-sm font-mono min-h-[100px]', placeholder: 'Ej: "¿Cómo puedo subir 20% los ingresos de Vázquez Mella el próximo trimestre?" o "Revisa mis 3 negocios y dime qué priorizar esta semana"' }),
      h('div', { class: 'flex justify-end mt-3' }, [
        h('button', { id: 'master-ask', class: 'px-5 py-2 bg-gold-500 hover:bg-gold-400 text-base-900 font-semibold rounded' }, '💬 Preguntar al maestro'),
      ]),
    ]),

    h('div', { id: 'master-result', class: 'mb-8' }),

    // Org chart visual
    h('h3', { class: 'font-display text-xl font-bold mb-4 mt-8' }, '🏛️ Organigrama vivo'),
    h('div', { id: 'org-chart', class: 'space-y-4' }),
  ]);
  root.appendChild(wrap);

  // Load org chart
  try {
    const r = await apiClient.get('/api/v1/master/org-chart');
    renderChart(wrap.querySelector('#org-chart'), r);
  } catch (e) {
    wrap.querySelector('#org-chart').appendChild(h('div', { class: 'text-rose-400 text-sm' }, 'Error org-chart: ' + (e?.message || e)));
  }

  // Hook ask button
  const askBtn = wrap.querySelector('#master-ask');
  const qInput = wrap.querySelector('#master-q');
  const resultBox = wrap.querySelector('#master-result');
  askBtn.addEventListener('click', async () => {
    const q = qInput.value.trim();
    if (!q) return;
    askBtn.disabled = true;
    askBtn.textContent = '🧠 Triando...';
    resultBox.innerHTML = '';
    resultBox.appendChild(h('div', { class: 'rounded-lg border border-gold-500/30 bg-gold-500/5 p-4 text-sm' }, [
      h('div', { class: 'text-gold-300 flex items-center gap-2' }, [
        h('span', { class: 'inline-block w-2 h-2 bg-gold-400 rounded-full animate-pulse' }),
        h('span', {}, 'Consultando especialistas en paralelo...'),
      ]),
    ]));
    try {
      const budget = wrap.querySelector('#master-budget').value;
      const r = await apiClient.post('/api/v1/master/ask', { query: q, thinkBudget: budget });
      resultBox.innerHTML = '';
      if (r?.ok && r.result) {
        renderResult(resultBox, r.result);
      } else {
        resultBox.appendChild(h('div', { class: 'text-rose-400 text-sm' }, 'Sin resultado'));
      }
    } catch (e) {
      resultBox.innerHTML = '';
      resultBox.appendChild(h('div', { class: 'text-rose-400 text-sm' }, 'Error: ' + (e?.message || e)));
    } finally {
      askBtn.disabled = false;
      askBtn.textContent = '💬 Preguntar al maestro';
    }
  });
}

function renderResult(parent, r) {
  parent.appendChild(h('div', { class: 'rounded-xl border border-gold-500/40 bg-gradient-to-br from-gold-500/10 to-transparent p-5 mb-3' }, [
    h('div', { class: 'flex items-center justify-between mb-3' }, [
      h('div', { class: 'flex items-center gap-2 text-gold-300' }, [
        h('span', {}, '🧠'),
        h('strong', {}, 'Respuesta del maestro'),
      ]),
      h('span', { class: 'text-xs text-ink-400' }, `${r.totalMs}ms · ${r.finalProvider}/${r.finalModel}`),
    ]),
    h('div', { class: 'whitespace-pre-wrap text-ink-100 leading-relaxed' }, r.finalAnswer),
  ]));

  if (r.triage?.reasoning) {
    parent.appendChild(h('div', { class: 'text-xs text-ink-400 mb-3' }, [
      h('strong', {}, 'Triage: '),
      h('span', {}, r.triage.reasoning),
    ]));
  }

  if (r.opinions?.length) {
    const opsBox = h('details', { class: 'rounded-lg border border-base-700 bg-base-900/40' }, [
      h('summary', { class: 'cursor-pointer px-4 py-2 text-sm text-ink-300 hover:text-gold-300' }, `Ver opiniones individuales (${r.opinions.length} especialistas)`),
      h('div', { class: 'p-4 space-y-3' },
        r.opinions.map((o) => h('div', { class: 'border-l-2 border-gold-500/30 pl-3' }, [
          h('div', { class: 'flex items-center gap-2 text-sm text-gold-300 mb-1' }, [
            h('span', {}, o.emoji),
            h('strong', {}, o.agentName),
            h('span', { class: 'text-xs text-ink-500 ml-auto' }, `${o.ms}ms`),
          ]),
          h('div', { class: 'text-sm text-ink-200 whitespace-pre-wrap' }, o.opinion),
        ]))
      ),
    ]);
    parent.appendChild(opsBox);
  }
}

function renderChart(parent, r) {
  if (!r?.divisions?.length) {
    parent.appendChild(h('div', { class: 'text-ink-400 text-sm' }, 'Sin organigrama'));
    return;
  }
  // CEO + COO header
  parent.appendChild(h('div', { class: 'rounded-xl border-2 border-gold-500 bg-gradient-to-br from-gold-500/20 to-base-900 p-4 mb-4' }, [
    h('div', { class: 'flex items-center gap-3' }, [
      h('span', { class: 'text-3xl' }, '🧠'),
      h('div', {}, [
        h('div', { class: 'font-display text-lg font-bold text-gold-300' }, 'CEO: Asistente CARLOS OS'),
        h('div', { class: 'text-xs text-ink-300' }, `Conoce a los ${r.totalAgents} agentes · enruta y sintetiza`),
      ]),
    ]),
  ]));

  for (const block of r.divisions) {
    const div = block.division;
    const card = h('div', { class: 'rounded-xl border border-base-700 bg-base-900/60 p-4' }, [
      h('div', { class: 'flex items-center gap-2 mb-3' }, [
        h('span', { class: 'text-2xl' }, div.emoji),
        h('strong', { class: 'text-lg' }, div.name),
        h('span', { class: 'text-xs text-ink-400 ml-auto' }, `${block.vps.length} VPs`),
      ]),
      h('div', { class: 'text-xs text-ink-400 mb-3' }, div.decisionScope),
      h('div', { class: 'grid md:grid-cols-2 gap-2' },
        block.vps.map((vp) => h('div', { class: 'rounded border border-base-800 bg-base-800/40 p-3' }, [
          h('div', { class: 'flex items-center gap-2 text-sm font-semibold text-gold-300' }, [
            h('span', {}, vp.vp.emoji),
            h('span', {}, vp.vp.name),
          ]),
          h('div', { class: 'text-xs text-ink-400 mt-1' }, vp.vp.decisionScope),
          h('div', { class: 'flex flex-wrap gap-1 mt-2' },
            vp.sampleAgents.map((a) => h('span', { class: 'text-xs px-2 py-0.5 bg-base-700/60 rounded' }, `${a.emoji} ${a.name}`))
          ),
        ]))
      ),
    ]);
    parent.appendChild(card);
  }
}
